import { useState, useEffect, useMemo, useCallback } from 'react'
import { Chip } from '@mui/material'
import { reportsService } from '../../../../services'
import { DEFAULT_ROWS } from '../../constants'
import { fmtAmount, fmtSaleDateTime, getSaleOrderListTimestamp } from '../../formatters'

function getErrorMessage(error, fallback) {
  const message =
    error?.response?.data?.message ||
    error?.message ||
    fallback
  return typeof message === 'string' ? message : fallback
}

export function useInvoiceTable({
  enabled,
  salesFilterParams,
  sortBy,
  sortOrder,
  onSort,
  resetPaginationKey,
  onError,
}) {
  const [state, setState] = useState({
    items: [],
    pagination: { total: 0, page: 1, limit: DEFAULT_ROWS },
    loading: false,
  })

  useEffect(() => {
    if (!resetPaginationKey) return
    setState((s) =>
      s.pagination.page === 1
        ? s
        : { ...s, pagination: { ...s.pagination, page: 1 } },
    )
  }, [resetPaginationKey])

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    const run = async () => {
      setState((s) => ({ ...s, loading: true }))
      try {
        const res = await reportsService.getSalesInvoiceWiseReport({
          ...salesFilterParams,
          page: state.pagination.page,
          limit: state.pagination.limit,
        })
        if (!cancelled) {
          setState((s) => ({
            ...s,
            items: res.items || [],
            pagination: res.pagination || s.pagination,
            loading: false,
          }))
        }
      } catch (error) {
        console.error('Failed to load invoice-wise sales:', error)
        if (!cancelled) {
          setState((s) => ({
            ...s,
            loading: false,
            items: s.items.length ? s.items : [],
          }))
          onError?.(getErrorMessage(error, 'Could not load invoice-wise sales.'))
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    enabled,
    salesFilterParams,
    state.pagination.page,
    state.pagination.limit,
    onError,
  ])

  const setPage = useCallback((page) => {
    setState((s) => ({
      ...s,
      pagination: { ...s.pagination, page },
    }))
  }, [])

  const setLimit = useCallback((limit) => {
    setState((s) => ({
      ...s,
      pagination: { ...s.pagination, page: 1, limit },
    }))
  }, [])

  const columns = useMemo(
    () => [
      { id: 'orderId', header: 'Order ID', sortField: 'orderId' },
      { id: 'orgName', header: 'Shop', sortField: 'orgName' },
      { id: 'orderStatus', header: 'Status', sortField: 'orderStatus' },
      { id: 'paymentStatus', header: 'Payment', sortField: 'paymentStatus' },
      { id: 'amount', header: 'Amount', sortField: 'amount' },
      { id: 'dateModified', header: 'Date Modified', sortField: 'dateModified' },
    ],
    [],
  )

  const rows = useMemo(
    () =>
      state.items.map((item) => ({
        ...item,
        orderStatus: (
          <Chip size="small" label={item.orderStatus || 'N/A'} />
        ),
        paymentStatus: item.paymentStatus || 'N/A',
        amount: fmtAmount(item.amount),
        dateModified: fmtSaleDateTime(getSaleOrderListTimestamp(item)),
      })),
    [state.items],
  )

  return {
    items: state.items,
    rows,
    columns,
    loading: state.loading,
    pagination: state.pagination,
    setPage,
    setLimit,
    sortBy,
    sortOrder,
    onSort,
  }
}
